// Corrections in the books (gap #17): reversing a journal entry, the accountant pack, and the words
// and inputs behind them, kept apart from the screens so they are tested. Who sees which control is
// the permission its action asks for (packages/auth permissions.ts), never a role name (QA-681).

import type { JournalEntry } from './types.ts';

/** accounting.post (journal.reverse): the owner, the manager and the bookkeeper. */
export const REVERSE_PERMS = ['accounting.post'];
/** accounting.close (periods.close, bank.reopenReconciliation): the owner, the manager and the bookkeeper. */
export const CLOSE_PERMS = ['accounting.close'];

/** What reopening a bank reconciliation does, before the person decides. */
export const reopenOutcome = (through: string) =>
  `The reconciliation through ${through} is reopened: its lines unlock so a wrong match can be corrected, and "reconciled through" goes back to the one before. Start a new reconciliation to finish it again.`;
/** reports.view (exports.accountantPack): the owner, the manager and the bookkeeper. */
export const PACK_PERMS = ['reports.view'];

/**
 * Whether the office reverses this entry here. Not one already reversed, not a reversal itself, and
 * not one a document posted (an invoice, a bill): those are corrected by voiding or crediting the
 * document, as the server says. A bank transaction's entry is reversed here.
 */
export function reversible(
  e: Pick<JournalEntry, 'reversedBy' | 'reversalOf' | 'source' | 'documentType'>,
): boolean {
  if (e.reversedBy || e.reversalOf) return false;
  return !(e.source === 'posting_rule' && e.documentType && e.documentType !== 'bank_transaction');
}

/** A document type with its article: "an invoice", "a vendor bill" (QA-382). */
export function documentWords(type: string): string {
  const words = type.replace(/_/g, ' ');
  return `${/^[aeiou]/i.test(words) ? 'an' : 'a'} ${words}`;
}

/** Why an entry offers no Reverse, in the office's words; null when it does. */
export function notReversibleWhy(
  e: Pick<JournalEntry, 'reversedBy' | 'reversalOf' | 'source' | 'documentType'>,
): string | null {
  if (e.reversedBy) return 'Reversed.';
  if (e.reversalOf) return 'This entry reverses another one.';
  if (!reversible(e)) return `Posted from ${documentWords(e.documentType!)}: correct the document instead.`;
  return null;
}

/** What a reversal did, from journal.reverse's answer: dated as asked, or in the first open month. */
export function reversedText(
  r: { number: string; postingDate: string; moved: boolean },
  dayText: (d: string) => string,
): string {
  return r.moved
    ? `Reversed by ${r.number}, posted ${dayText(r.postingDate)}: the month you picked is closed, so it went in the first open month.`
    : `Reversed by ${r.number}, posted ${dayText(r.postingDate)}.`;
}

// ── the accountant pack ──

export interface PackFile {
  name: string;
  contentType: 'text/csv';
  content: string;
}

/** "From must be on or before To", or null when the range can be asked for. */
export function rangeProblem(from: string, to: string): string | null {
  if (!from || !to) return 'Pick both dates.';
  if (from > to) return 'The start is after the end.';
  return null;
}

/** The pack's file names carry their dates already ("trial-balance-2026-10-31.csv"); saved as named. */
const LABELS: Record<string, string> = {
  'trial-balance': 'Trial balance',
  'profit-and-loss': 'Profit and loss',
  'balance-sheet': 'Balance sheet',
  'cash-flow': 'Cash flow',
  'general-ledger': 'General ledger detail',
  'ar-aging': 'Receivables aging',
  'tax-returns': 'Tax returns',
  'bank-reconciliations': 'Bank reconciliations',
};

/** "trial-balance-2026-10-31.csv" → "Trial balance"; an unknown file by its own words. */
export function packFileLabel(name: string): string {
  const stem = name.replace(/\.csv$/i, '').replace(/(-\d{4}-\d{2}-\d{2})+$/, '');
  const known = LABELS[stem];
  if (known) return known;
  const words = stem.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "1 row", "12 rows", "No rows" under a file's header line. */
export function rowsText(csv: string): string {
  const n = Math.max(0, csv.trim().split('\n').length - 1);
  return n === 0 ? 'No rows' : `${n} ${n === 1 ? 'row' : 'rows'}`;
}

// ── months: reopening one (periods.reopen) ──

/** accounting.reopen: the owner, or a role of the owner's own that gives it (owner-only in the catalogue). */
export const REOPEN_PERMS = ['accounting.reopen'];

export interface Period {
  id: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
  closedAt: string | null;
}

/** The newest closed month: only it can be reopened (the server: later months first). */
export const reopenable = (periods: readonly Period[]): Period | null =>
  [...periods]
    .filter((p) => p.status === 'closed')
    .sort((a, b) => b.startDate.localeCompare(a.startDate))[0] ?? null;

/** What reopening does, said before the owner confirms (books' words): what it changes and what it doesn't. */
export const reopenWarning = (month: string) =>
  `Reopening ${month} lets entries post into it again. Its reports and tax figures can change. A sales tax return already filed, an export already sent to your accounting software, and posted payroll aren’t changed: if you post into this month, check them. Close it again when you’re done.`;

// ── imports: opening balances and history summaries ──

/** accounting.post (imports.openingBalances, imports.historySummaries): owner, manager, bookkeeper. */
export const IMPORT_PERMS = ['accounting.post'];

/** A CSV's cells, row by row: commas, double-quoted cells with "" for a quote, blank lines dropped. */
export function csvRows(text: string): string[][] {
  const out: string[][] = [];
  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (!line.trim()) continue;
    const cells: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]!;
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (ch === '"') quoted = false;
        else cur += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === ',') {
        cells.push(cur.trim());
        cur = '';
      } else cur += ch;
    }
    cells.push(cur.trim());
    out.push(cells);
  }
  return out;
}

const money = (s: string) => {
  const t = s.replace(/[$,\s]/g, '');
  if (!t) return 0;
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : null;
};

export interface TrialRows {
  rows: { accountCode: string; debitCents: number; creditCents: number }[];
  problems: string[];
  debitCents: number;
  creditCents: number;
}

/**
 * An old trial balance as account code, debit, credit (a header row is skipped). Amounts in dollars;
 * a blank debit or credit is 0. The server's own check ("does not balance") is the last word.
 */
export function trialRows(text: string): TrialRows {
  const all = csvRows(text);
  const body = all.length && !/\d/.test(all[0]!.slice(1).join('')) ? all.slice(1) : all;
  const rows: TrialRows['rows'] = [];
  const problems: string[] = [];
  body.forEach((c, i) => {
    const at = `Line ${i + 1 + (body === all ? 0 : 1)}`;
    const [code = '', d = '', cr = ''] = c;
    const debit = money(d);
    const credit = money(cr);
    if (!code) problems.push(`${at}: no account code.`);
    else if (debit === null || credit === null) problems.push(`${at}: amounts like 1250.00.`);
    else if (debit && credit) problems.push(`${at}: a debit or a credit, not both.`);
    else rows.push({ accountCode: code, debitCents: debit, creditCents: credit });
  });
  return {
    rows,
    problems,
    debitCents: rows.reduce((s, r) => s + r.debitCents, 0),
    creditCents: rows.reduce((s, r) => s + r.creditCents, 0),
  };
}

/** A tie-out figure typed in dollars; null when it isn't one (they're all required). */
export const tieCents = (s: string): number | null => {
  const t = s.replace(/[$,\s]/g, '').replace('−', '-');
  return /^-?\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : null;
};

/** imports.historySummaries' metrics, in the office's words. */
export const HISTORY_METRICS: { value: string; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'cost_of_goods', label: 'Cost of goods' },
  { value: 'labour', label: 'Labour' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'gross_profit', label: 'Gross profit' },
  { value: 'ar_balance', label: 'Receivables balance' },
  { value: 'jobs_completed', label: 'Jobs completed' },
];

/** The server's rule for where the numbers came from: a short lowercase name, like servicetitan. */
export const SOURCE = /^[a-z][a-z_]{1,30}$/;

/** The systems history usually comes from, in words; the server keeps the code. */
export const HISTORY_SOURCES = [
  { code: 'servicetitan', label: 'ServiceTitan' },
  { code: 'quickbooks_desktop', label: 'QuickBooks Desktop' },
  { code: 'quickbooks_online', label: 'QuickBooks Online' },
  { code: 'xero', label: 'Xero' },
  { code: 'jobber', label: 'Jobber' },
  { code: 'housecall_pro', label: 'Housecall Pro' },
  { code: 'spreadsheet', label: 'A spreadsheet' },
] as const;

/** Another system's name, as typed ("Field Edge 2"), made into the server's code (field_edge): null if no letters. */
export function sourceCode(name: string): string | null {
  const code = name
    .toLowerCase()
    .replace(/[^a-z]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 31)
    .replace(/_+$/, '');
  return SOURCE.test(code) ? code : null;
}

export interface HistoryRows {
  rows: { month: string; metric: string; amountCents: number; businessUnitId?: string }[];
  problems: string[];
}

/**
 * Monthly totals as month (2025-09), metric, amount, and an optional department by name; a header
 * row is skipped. A metric may be its label or its key. Jobs completed is a count, sent as it is.
 */
export function historyRows(text: string, units: readonly { id: string; name: string }[]): HistoryRows {
  const all = csvRows(text);
  // a header row starts with a word ("Month"), a data row with a year
  const body = all.length && /^[A-Za-z]/.test(all[0]![0] ?? '') ? all.slice(1) : all;
  const rows: HistoryRows['rows'] = [];
  const problems: string[] = [];
  body.forEach((c, i) => {
    const at = `Line ${i + 1 + (body === all ? 0 : 1)}`;
    const [month = '', m = '', amount = '', unit = ''] = c;
    const metric = HISTORY_METRICS.find(
      (x) => x.value === m.trim().toLowerCase() || x.label.toLowerCase() === m.trim().toLowerCase(),
    );
    const cents =
      metric?.value === 'jobs_completed'
        ? /^\d+$/.test(amount.trim())
          ? Number(amount)
          : null
        : tieCents(amount);
    const bu = unit.trim()
      ? units.find((u) => u.name.toLowerCase() === unit.trim().toLowerCase())
      : undefined;
    if (!/^\d{4}-\d{2}$/.test(month)) problems.push(`${at}: a month like 2025-09.`);
    else if (!metric) problems.push(`${at}: “${m}” isn’t one of the figures kept.`);
    else if (cents === null) problems.push(`${at}: an amount like 48250.00.`);
    else if (unit.trim() && !bu) problems.push(`${at}: there’s no department “${unit.trim()}”.`);
    else
      rows.push({
        month,
        metric: metric.value,
        amountCents: cents,
        ...(bu ? { businessUnitId: bu.id } : {}),
      });
  });
  return { rows, problems };
}

// ── the chart of accounts from the old books (imports.chartOfAccounts) ──

/** Our account types, as Setup names them, with the words other books use for each. */
export const CHART_TYPES: { subtype: string; label: string; also: string[] }[] = [
  { subtype: 'bank', label: 'Bank', also: ['cash', 'chequing', 'checking', 'savings'] },
  {
    subtype: 'current_asset',
    label: 'Current asset',
    also: ['accounts receivable', 'other current asset', 'receivable', 'a/r'],
  },
  { subtype: 'inventory', label: 'Inventory', also: ['stock'] },
  { subtype: 'fixed_asset', label: 'Fixed asset', also: ['other asset', 'equipment', 'property'] },
  {
    subtype: 'current_liability',
    label: 'Current liability',
    also: ['accounts payable', 'other current liability', 'payable', 'a/p'],
  },
  { subtype: 'credit_card', label: 'Credit card', also: ['credit card', 'visa', 'mastercard', 'amex'] },
  { subtype: 'long_term_liability', label: 'Long-term liability', also: ['long term liability', 'loan'] },
  { subtype: 'equity', label: 'Equity', also: ['owner equity', 'owners equity', 'capital'] },
  { subtype: 'income', label: 'Income', also: ['revenue', 'sales'] },
  { subtype: 'other_income', label: 'Other income', also: [] },
  {
    subtype: 'cost_of_sales',
    label: 'Cost of jobs',
    also: ['cost of goods sold', 'cogs', 'cost of sales', 'direct costs'],
  },
  { subtype: 'expense', label: 'Running cost', also: ['expense', 'expenses', 'other expense', 'overhead'] },
];

const typeKey = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
/** An old system's account type, in its words or ours, as one of our subtypes; null when it isn't one. */
export function chartSubtype(word: string): string | null {
  const w = typeKey(word);
  const t = CHART_TYPES.find(
    (x) => x.subtype === w.replace(/[ -]/g, '_') || typeKey(x.label) === w || x.also.includes(w),
  );
  return t?.subtype ?? null;
}
export const chartTypeLabel = (subtype: string) =>
  CHART_TYPES.find((t) => t.subtype === subtype)?.label ?? subtype;

export interface ChartRows {
  rows: { code: string; name: string; subtype: string; externalId?: string }[];
  problems: string[];
}

/**
 * The old chart as code, name, type, and the old system's id (optional); a header row is skipped.
 * The type may be ours ("Running cost") or the old system's ("Expense", "Cost of Goods Sold"). An
 * account whose code is already in the chart is left as it is (the server's rule).
 */
export function chartRows(text: string): ChartRows {
  const all = csvRows(text);
  // a header row's type is a heading ("Type"), not one we know
  const body =
    all.length && /^(code|account|number|no\.?|#)$/i.test(all[0]![0]?.trim() ?? '') ? all.slice(1) : all;
  const rows: ChartRows['rows'] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  body.forEach((c, i) => {
    const at = `Line ${i + 1 + (body === all ? 0 : 1)}`;
    const [code = '', name = '', type = '', ext = ''] = c.map((x) => x.trim());
    const subtype = chartSubtype(type);
    if (!/^[A-Za-z0-9._-]{1,20}$/.test(code))
      problems.push(`${at}: a code like 6100 (letters, digits, dot, dash).`);
    else if (!name) problems.push(`${at}: the account’s name.`);
    else if (name.length > 120) problems.push(`${at}: a name of 120 characters or fewer.`);
    else if (!subtype)
      problems.push(
        `${at}: “${type}” isn’t a type we know; use one of ${CHART_TYPES.map((t) => t.label).join(', ')}.`,
      );
    else if (seen.has(code.toLowerCase())) problems.push(`${at}: ${code} is in the file twice.`);
    else {
      seen.add(code.toLowerCase());
      rows.push({ code, name, subtype, ...(ext ? { externalId: ext.slice(0, 100) } : {}) });
    }
  });
  return { rows, problems };
}
